import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Chip, TableSortLabel, useTheme, LinearProgress, Box, Tooltip, Avatar } from "@mui/material";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { FinanceType, FinanceTypeDef } from "../../../../models/finance/FinanceType";
import {
  EXPENSES_ROUTE_PATH,
  INCOMES_ROUTE_PATH,
} from "../../../../utils/constants/AppConstants";
import { useCurrencyFormatter } from "../../../../contexts/CurrencyContext";
import { TrendingUp, TrendingDown } from "@mui/icons-material";
import { BudgetStorage } from "../../../../utils/BudgetStorage";

export interface MonthlyReportsCategoryTableRow {
  categoryName: string;
  actual: number;
  planned: number;
  difference: number;
}

interface MonthlyReportsCategoryTableProps {
  rows: MonthlyReportsCategoryTableRow[];
  type: FinanceTypeDef;
  year: number;
  month: string;
  isCompact?: boolean;
}

const MonthlyReportsCategoryTableComponent: React.FC<
  MonthlyReportsCategoryTableProps
> = (props) => {
  const { formatCurrencyNumber } = useCurrencyFormatter();
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    (searchParams.get("sortOrder") as "asc" | "desc") || "desc"
  );

  const [sortBy, setSortBy] = useState<
    "category" | "actual" | "planned" | "difference"
  >(
    (searchParams.get("sortBy") as
      | "category"
      | "actual"
      | "planned"
      | "difference") || "actual"
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const sortedData = [...props.rows].sort((a, b) => {
    const multiplier = sortOrder === "asc" ? 1 : -1;
    if (sortBy === "category") {
      return a.categoryName.localeCompare(b.categoryName) * multiplier;
    } else if (sortBy === "actual") {
      return (a.actual - b.actual) * multiplier;
    } else if (sortBy === "planned") {
      return (a.planned - b.planned) * multiplier;
    } else if (sortBy === "difference") {
      return (a.difference - b.difference) * multiplier;
    }

    return 0;
  });

  const rows = sortedData.map((row, index) => {
    // Get planned amount from budget storage
    const isExpense = props.type === FinanceType.EXPENSE;
    const planned = BudgetStorage.getCategoryBudget(props.year, props.month, row.categoryName, isExpense);
    const difference = row.actual - planned;
    
    return {
      id: index,
      row: {
        ...row,
        planned,
        difference,
      },
    };
  });

  const rowsPerPage = props.isCompact ? 5 : 8;
  const paginatedRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleSort = (
    column: "category" | "actual" | "planned" | "difference"
  ) => {
    let newSortOrder: "asc" | "desc" = "desc";
    if (sortBy === column) {
      newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    }

    setSortBy(column);
    setSortOrder(newSortOrder);

    searchParams.set("sortBy", column);
    searchParams.set("sortOrder", newSortOrder);
    setSearchParams(searchParams);
  };

  const handleCategoryClick = (categoryName: string) => {
    const basePath =
      props.type === FinanceType.EXPENSE
        ? EXPENSES_ROUTE_PATH
        : INCOMES_ROUTE_PATH;

    const queryParams = new URLSearchParams({
      year: props.year.toString(),
      month: props.month,
      category: categoryName,
    });

    navigate(`${basePath}?${queryParams.toString()}`);
  };

  // Calculate total for percentage calculations
  const totalActual = props.rows.reduce((sum, row) => sum + row.actual, 0);

  // Get category color based on index
  const getCategoryColor = (index: number) => {
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.info.main,
    ];
    return colors[index % colors.length];
  };

  const isExpense = props.type === FinanceType.EXPENSE;

  if (props.rows.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: props.isCompact ? 4 : 6 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No categories found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No {isExpense ? 'expenses' : 'income'} recorded for this period
        </Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ overflow: 'hidden', borderRadius: 2 }}>
      <TableContainer>
        <Table size={props.isCompact ? "small" : "medium"}>
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
              <TableCell sx={{ fontWeight: 600, width: props.isCompact ? '35%' : '30%' }}>
                <TableSortLabel
                  active={sortBy === "category"}
                  direction={sortBy === "category" ? sortOrder : "asc"}
                  onClick={() => handleSort("category")}
                >
                  Category
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600, width: props.isCompact ? '30%' : '25%' }}>
                <TableSortLabel
                  active={sortBy === "actual"}
                  direction={sortBy === "actual" ? sortOrder : "asc"}
                  onClick={() => handleSort("actual")}
                >
                  Amount
                </TableSortLabel>
              </TableCell>
              {!props.isCompact && (
                <TableCell sx={{ fontWeight: 600, width: '20%' }}>
                  Percentage
                </TableCell>
              )}
              <TableCell sx={{ fontWeight: 600, width: props.isCompact ? '35%' : '25%' }}>
                <TableSortLabel
                  active={sortBy === "planned"}
                  direction={sortBy === "planned" ? sortOrder : "asc"}
                  onClick={() => handleSort("planned")}
                >
                  {props.isCompact 
                    ? (props.type === FinanceType.EXPENSE ? 'Budget' : 'Target')
                    : (props.type === FinanceType.EXPENSE ? 'Budget vs Actual' : 'Target vs Actual')
                  }
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((indexedRow, index) => {
              const percentage = totalActual > 0 ? (indexedRow.row.actual / totalActual) * 100 : 0;
              const categoryColor = getCategoryColor(index);
              
              return (
                <TableRow 
                  key={indexedRow.id}
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: theme.palette.action.hover,
                      cursor: 'pointer',
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: theme.palette.action.selected + '20',
                    },
                  }}
                  onClick={() => handleCategoryClick(indexedRow.row.categoryName)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: props.isCompact ? 1 : 2 }}>
                      <Avatar
                        sx={{
                          width: props.isCompact ? 24 : 32,
                          height: props.isCompact ? 24 : 32,
                          backgroundColor: categoryColor + '20',
                          color: categoryColor,
                          fontSize: props.isCompact ? '0.65rem' : '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        {indexedRow.row.categoryName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography 
                          variant={props.isCompact ? "body2" : "body2"} 
                          sx={{ fontWeight: 500, fontSize: props.isCompact ? '0.85rem' : undefined }}
                        >
                          {indexedRow.row.categoryName}
                        </Typography>
                        {!props.isCompact && (
                          <Typography variant="caption" color="text.secondary">
                            Click to view details
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 600,
                          color: isExpense ? theme.palette.error.main : theme.palette.success.main,
                          fontSize: props.isCompact ? '0.85rem' : undefined,
                        }}
                      >
                        {formatCurrencyNumber(indexedRow.row.actual)}
                      </Typography>
                      {props.isCompact && (
                        <Typography variant="caption" color="text.secondary">
                          {percentage.toFixed(1)}%
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  
                  {!props.isCompact && (
                    <TableCell>
                      <Box sx={{ minWidth: 120 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {percentage.toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: theme.palette.grey[200],
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: categoryColor,
                              borderRadius: 3,
                            },
                          }}
                        />
                      </Box>
                    </TableCell>
                  )}
                  
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {indexedRow.row.planned > 0 ? (
                        <>
                          <Tooltip title={`${props.type === FinanceType.EXPENSE ? 'Budget' : 'Target'}: ${formatCurrencyNumber(indexedRow.row.planned)}`}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {(() => {
                                const isExpense = props.type === FinanceType.EXPENSE;
                                const isActualHigher = indexedRow.row.actual > indexedRow.row.planned;
                                
                                // For expenses: higher than budget is bad (red), lower is good (green)
                                // For income: higher than target is good (green), lower is bad (red)
                                const isPositiveVariance = isExpense ? !isActualHigher : isActualHigher;
                                const iconColor = isPositiveVariance ? theme.palette.success.main : theme.palette.error.main;
                                const textColor = isPositiveVariance ? theme.palette.success.main : theme.palette.error.main;
                                
                                return (
                                  <>
                                    {isActualHigher ? (
                                      <TrendingUp sx={{ fontSize: props.isCompact ? 14 : 16, color: iconColor }} />
                                    ) : (
                                      <TrendingDown sx={{ fontSize: props.isCompact ? 14 : 16, color: iconColor }} />
                                    )}
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        fontWeight: 500,
                                        fontSize: props.isCompact ? '0.8rem' : undefined,
                                        color: textColor,
                                      }}
                                    >
                                      {formatCurrencyNumber(Math.abs(indexedRow.row.difference))}
                                    </Typography>
                                  </>
                                );
                              })()}
                            </Box>
                          </Tooltip>
                          {!props.isCompact && (
                            <Chip 
                              label={indexedRow.row.actual > indexedRow.row.planned 
                                ? `${props.type === FinanceType.EXPENSE ? 'Over' : 'Above'}`
                                : `${props.type === FinanceType.EXPENSE ? 'Under' : 'Below'}`
                              }
                              size="small"
                              color={(() => {
                                const isExpense = props.type === FinanceType.EXPENSE;
                                const isActualHigher = indexedRow.row.actual > indexedRow.row.planned;
                                // For expenses: over budget is bad, under is good
                                // For income: above target is good, below is bad
                                const isPositive = isExpense ? !isActualHigher : isActualHigher;
                                return isPositive ? "success" : "error";
                              })()}
                              sx={{ 
                                height: 20, 
                                fontSize: '0.7rem',
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: props.isCompact ? '0.8rem' : undefined }}>
                          No {props.type === FinanceType.EXPENSE ? 'budget' : 'target'} set
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {props.rows.length > rowsPerPage && (
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[]}
          sx={{ 
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.grey[50],
          }}
        />
      )}
    </Paper>
  );
};

export default MonthlyReportsCategoryTableComponent;
